import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { toast } from "react-toastify";
import { userService } from "../../../services/userService";
import { partnerService } from "../../../services/partnerService";
import { getCurrentUserPartnerUuid, isCurrentUserAdmin } from "../../../util/auth";
import PartnerSelector from "./PartnerSelector";
import UserInfoBlock from "./UserInfoBlock";

const UserModal = ({
  isOpen,
  toggle,
  mode = "create",
  userData = null,
  onSuccess,
  onDelete,
  onToggleActive,
  onResetPassword,
}) => {
  const isAdmin = isCurrentUserAdmin();

  // MANAGERs can create USER and MANAGER accounts, but not ADMIN
  const canAssignAdmin = isAdmin;

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    userType: "USER",
    partnerUuid: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [partnerSearchTerm, setPartnerSearchTerm] = useState("");

  const fetchPartners = async (searchTerm = "") => {
    setLoadingPartners(true);
    try {
      const params = {
        page: 0,
        size: 100,
        ...(searchTerm?.trim() && { name: searchTerm.trim() }),
      };
      const response = await partnerService.list(params);
      setPartners(response.content || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
      toast.error("Failed to load partners");
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handlePartnerSearch = (searchTerm) => {
    setPartnerSearchTerm(searchTerm);
    fetchPartners(searchTerm);
  };

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" || mode === "view") {
        setFormData({
          username: userData?.userName || "",
          email: userData?.email || "",
          userType: userData?.userType || "USER",
          partnerUuid: userData?.partnerUuid || "",
        });
      } else {
        setFormData({
          username: "",
          email: "",
          userType: "USER",
          partnerUuid: getCurrentUserPartnerUuid() || "",
        });
      }
      setErrors({});
      setPartnerSearchTerm("");
      if (mode !== "view" && isAdmin) {
        fetchPartners();
      }
    }
  }, [isOpen, mode, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.userType) {
      newErrors.userType = "User type is required";
    }

    if (!formData.partnerUuid.trim()) {
      newErrors.partnerUuid = "Partner UUID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === "view") {
      toggle();
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    setLoading(true);

    try {
      let response;
      const payload = (!canAssignAdmin && formData.userType === "ADMIN")
        ? { ...formData, userType: "MANAGER" }
        : formData;

      if (mode === "create") {
        response = await userService.create(payload);
        toast.success("User created successfully");
      } else if (mode === "edit") {
        response = await userService.update(userData.id, payload);
        toast.success("User updated successfully");
      }

      if (onSuccess) {
        onSuccess(response);
      }

      toggle();
    } catch (error) {
      console.error("Error saving user:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to ${mode === "create" ? "create" : "update"} user`);
      }
    } finally {
      setLoading(false);
    }
  };

  const getModalTitle = () => {
    switch (mode) {
      case "create": return "Create New User";
      case "edit": return "Edit User";
      case "view": return "View User Details";
      default: return "User";
    }
  };

  const isReadOnly = mode === "view";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>{getModalTitle()}</ModalHeader>
      <>
        <ModalBody>
          {/* Username */}
          <FormGroup>
            <Label for="username">
              Username <span className="text-danger">*</span>
            </Label>
            <Input
              id="username"
              name="username"
              type="text"
              placeholder="Enter username"
              value={formData.username}
              onChange={(e) => handleChange("username", e.target.value)}
              invalid={!!errors.username}
              disabled={isReadOnly || mode === "edit"}
            />
            <FormFeedback>{errors.username}</FormFeedback>
            {mode === "edit" && (
              <small className="text-muted">Username cannot be changed</small>
            )}
          </FormGroup>

          {/* Email */}
          <FormGroup>
            <Label for="email">
              Email <span className="text-danger">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              invalid={!!errors.email}
              disabled={isReadOnly}
            />
            <FormFeedback>{errors.email}</FormFeedback>
          </FormGroup>

          {/* User Type */}
          <FormGroup>
            <Label for="userType">
              User Type <span className="text-danger">*</span>
            </Label>
            <Input
              id="userType"
              name="userType"
              type="select"
              value={formData.userType}
              onChange={(e) => handleChange("userType", e.target.value)}
              invalid={!!errors.userType}
              disabled={isReadOnly}
            >
              <option value="USER">User</option>
              <option value="MANAGER">Manager</option>
              {canAssignAdmin && <option value="ADMIN">Admin</option>}
            </Input>
            <FormFeedback>{errors.userType}</FormFeedback>
          </FormGroup>

          {/* Partner */}
          <FormGroup>
            <Label for="partnerUuid">
              Partner <span className="text-danger">*</span>
            </Label>
            {isReadOnly || !isAdmin ? (
              <Input
                id="partnerUuid"
                name="partnerUuid"
                type="text"
                value={formData.partnerUuid}
                disabled
              />
            ) : (
              <PartnerSelector
                value={formData.partnerUuid}
                onChange={(value) => handleChange("partnerUuid", value)}
                searchTerm={partnerSearchTerm}
                onSearch={handlePartnerSearch}
                partners={partners}
                loading={loading}
                loadingPartners={loadingPartners}
                errors={errors.partnerUuid}
              />
            )}
            <FormFeedback>{errors.partnerUuid}</FormFeedback>
            <small className="text-muted d-block mt-1">
              {isAdmin ? "Select the partner this user belongs to" : "Assigned to your partner automatically"}
            </small>
          </FormGroup>

          {/* Additional info in view/edit mode */}
          {(mode === "edit" || mode === "view") && (
            <UserInfoBlock userData={userData} />
          )}
        </ModalBody>

        <ModalFooter>
          {mode === "view" ? (
            <>
              <div className="d-flex gap-2 me-auto">
                {onDelete && (
                  <Button
                    type="button"
                    color="danger"
                    onClick={async () => {
                      setActionLoading("delete");
                      try { await onDelete(userData); } finally { setActionLoading(null); }
                    }}
                    disabled={!!actionLoading}
                    title="Permanently delete this user"
                  >
                    {actionLoading === "delete" && (
                      <span className="spinner-border spinner-border-sm me-1"></span>
                    )}
                    Delete
                  </Button>
                )}
                {onToggleActive && (
                  <Button
                    type="button"
                    color={userData?.active ? "warning" : "success"}
                    onClick={async () => {
                      setActionLoading("toggle");
                      try { await onToggleActive(userData); } finally { setActionLoading(null); }
                    }}
                    disabled={!!actionLoading}
                    title={userData?.active ? "Deactivate this user" : "Activate this user"}
                  >
                    {actionLoading === "toggle" && (
                      <span className="spinner-border spinner-border-sm me-1"></span>
                    )}
                    {userData?.active ? "Deactivate" : "Activate"}
                  </Button>
                )}
                {onResetPassword && (
                  <Button
                    type="button"
                    color="secondary"
                    onClick={async () => {
                      setActionLoading("reset");
                      try { await onResetPassword(userData); } finally { setActionLoading(null); }
                    }}
                    disabled={!!actionLoading}
                    title="Generate a new temporary password and email it to the user"
                  >
                    {actionLoading === "reset" && (
                      <span className="spinner-border spinner-border-sm me-1"></span>
                    )}
                    Reset Password
                  </Button>
                )}
              </div>
              <Button type="button" color="light" onClick={toggle} disabled={!!actionLoading}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button type="button" color="secondary" onClick={toggle} disabled={loading}>
                Cancel
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {mode === "create" ? "Creating..." : "Updating..."}
                  </>
                ) : mode === "create" ? (
                  "Create User"
                ) : (
                  "Update User"
                )}
              </Button>
            </>
          )}
        </ModalFooter>
      </>
    </Modal>
  );
};

export default UserModal;
